import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Hammer } from "@fleet/icons";
import { Card, CardContent, Label, Skeleton, Switch, TextField } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { adminQueryKeys, type components, type MaintenanceScope } from "@agape/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type MaintenanceStatus = components["schemas"]["MaintenanceStatusResponse"];
type SetMaintenance = components["schemas"]["SetMaintenanceDto"];

const SCOPE_ORDER: MaintenanceScope[] = ["writes", "sync", "all"];

/**
 * Operator switch for maintenance mode.
 *
 * Lives next to the feature flags because that is where it is STORED (the
 * `system.maintenance` row), but it has its own endpoint: activating it is
 * audited, and the generic flag CRUD refuses `system.*` keys precisely so this
 * cannot be flipped without leaving a trace.
 *
 * Reads are open to any back-office role; only `flags.write` (admin+) can act.
 */
export function MaintenancePanel() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const canWrite = () => can("flags.write");

  const [scopes, setScopes] = createSignal<MaintenanceScope[]>(["writes"]);
  const [message, setMessage] = createSignal("");

  const statusQuery = createApiQuery<MaintenanceStatus, ErrorModel>(
    () => adminQueryKeys.maintenance(),
    async () => api.GET("/api/admin/maintenance", {}),
    () => ({ staleTime: 5000 }),
  );

  const active = createMemo(() => statusQuery.data?.effective.active ?? false);
  const source = createMemo(() => statusQuery.data?.source ?? "default");
  const overridden = createMemo(() => statusQuery.data?.overridden ?? false);
  const effectiveScopes = createMemo(() => statusQuery.data?.effective.scopes ?? []);

  const setMutation = createApiMutation(async (vars: SetMaintenance) =>
    api.PUT("/api/admin/maintenance", { body: vars }),
  );

  const apply = (nextActive: boolean) => {
    setMutation.mutate(
      {
        active: nextActive,
        scopes: nextActive ? scopes() : [],
        message: nextActive && message().trim() !== "" ? message().trim() : null,
        until: null,
      },
      {
        onSuccess: () => {
          void statusQuery.refetch();
          showToast(
            nextActive ? t(msg`Maintenance mode ON`) : t(msg`Maintenance mode OFF`),
            "success",
          );
        },
      },
    );
  };

  const toggleScope = (scope: MaintenanceScope, on: boolean) => {
    setScopes((prev) => {
      const next = prev.filter((s) => s !== scope);
      return on ? [...next, scope] : next;
    });
  };

  // Lingui wants plain `${variable}` interpolation, so the value is bound to a
  // local before it reaches the macro.
  const sourceLabel = () => {
    const value = source();
    return t(msg`Source: ${value}`);
  };

  const overriddenLabel = () => {
    const value = source();
    return t(
      msg`This switch is overridden: the effective window comes from ${value}, not from the database. Clear MAINTENANCE_MODE or the switch file to regain control here.`,
    );
  };

  const scopeLabel = (scope: MaintenanceScope) => {
    switch (scope) {
      case "writes": {
        return t(msg`Writes (reads and downloads keep working)`);
      }
      case "sync": {
        return t(msg`Sync (PowerSync fully paused)`);
      }
      default: {
        return t(msg`Everything (all mutations blocked)`);
      }
    }
  };

  return (
    <div class="space-y-4" data-testid="maintenance-panel">
      <div class="flex items-center gap-3">
        <Hammer class="h-7 w-7 text-warning" />
        <div>
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Maintenance mode`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Pause writes or sync for a planned window. Leave the message empty for a silent window.`,
            )}
          </p>
        </div>
      </div>

      <Show when={statusQuery.isError}>
        <QueryErrorState
          testId="maintenance-panel-error"
          compact
          onRetry={() => void statusQuery.refetch()}
        />
      </Show>
      <Show when={!statusQuery.isError}>
        <Show when={!statusQuery.isLoading} fallback={<Skeleton class="h-40 w-full rounded-xl" />}>
          <Card>
            <CardContent class="p-4 space-y-4">
              <div class="flex items-center justify-between gap-4">
                <div class="min-w-0">
                  <div class="font-bold" data-testid="maintenance-state">
                    {active() ? t(msg`Active`) : t(msg`Off`)}
                  </div>
                  <div class="text-xs text-muted-foreground">
                    {sourceLabel()}
                    <Show when={active() && effectiveScopes().length > 0}>
                      {" · "}
                      {effectiveScopes().join(", ")}
                    </Show>
                  </div>
                </div>
                <Switch
                  checked={active()}
                  disabled={!canWrite() || setMutation.isPending}
                  onCheckedChange={apply}
                />
              </div>

              {/*
                The single most confusing moment of a real window: the operator
                flips the switch and nothing happens because an env var or the
                switch file outranks the database. Say so, out loud.
              */}
              <Show when={overridden()}>
                <div
                  class="rounded-lg bg-warning/20 text-warning p-3 text-sm"
                  data-testid="maintenance-overridden"
                >
                  {overriddenLabel()}
                </div>
              </Show>

              <Show when={canWrite()}>
                <div class="space-y-3 border-t pt-4">
                  <For each={SCOPE_ORDER}>
                    {(scope) => (
                      <div class="flex items-center justify-between gap-4">
                        <Label for={`maintenance-scope-${scope}`} class="text-sm">
                          {scopeLabel(scope)}
                        </Label>
                        <Switch
                          checked={scopes().includes(scope)}
                          onCheckedChange={(on: boolean) => {
                            toggleScope(scope, on);
                          }}
                        />
                      </div>
                    )}
                  </For>
                  <div class="space-y-2">
                    <Label for="maintenance-message">
                      {t(msg`Message shown to users (empty = silent window)`)}
                    </Label>
                    <TextField
                      id="maintenance-message"
                      value={message()}
                      onInput={(e: InputEvent & { currentTarget: HTMLInputElement }) => {
                        setMessage(e.currentTarget.value);
                      }}
                    />
                  </div>
                  <div class="flex justify-end">
                    <Button
                      disabled={setMutation.isPending || scopes().length === 0}
                      onClick={() => {
                        apply(true);
                      }}
                    >
                      {t(msg`Apply window`)}
                    </Button>
                  </div>
                </div>
              </Show>
            </CardContent>
          </Card>
        </Show>
      </Show>
    </div>
  );
}
